import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
interface PrivacyPageProps {
  onBack: () => void;
}
export function PrivacyPage({
  onBack
}: PrivacyPageProps) {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);
  return <div className="min-h-screen w-full bg-dark-bg font-inter antialiased" data-id="element-442">
      <div className="max-w-3xl mx-auto px-6 py-16 md:py-24" data-id="element-443">
        {/* Back button */}
        <motion.button onClick={onBack} className="inline-flex items-center gap-2 text-concrete/60 hover:text-orange-accent transition-colors duration-200 mb-12 font-inter text-sm" initial={{
        opacity: 0,
        x: -10
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-444">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-445" />
          Back to main page
        </motion.button>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-446">
          <h1 className="font-bebas text-4xl md:text-5xl text-white tracking-wide mb-2" data-id="element-447">
            PRIVACY <span className="text-orange-accent" data-id="element-448">POLICY</span>
          </h1>
          <p className="font-inter text-concrete/60 text-sm mb-12" data-id="element-449">
            Last updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>

          <div className="space-y-10 font-inter text-concrete leading-relaxed" data-id="element-450">
            <section data-id="element-451">
              <h2 className="font-bebas text-2xl text-white tracking-wide mb-3" data-id="element-452">
                1. OUR COMMITMENT TO YOUR PRIVACY
              </h2>
              <p data-id="element-453">
                We understand that privacy isn't just a preference for our
                customers — it's a core value. Concrete Bunker Blueprint is
                designed from the ground up to minimize data collection. We
                collect only what is strictly necessary to process your purchase
                and deliver your product.
              </p>
            </section>

            <section data-id="element-454">
              <h2 className="font-bebas text-2xl text-white tracking-wide mb-3" data-id="element-455">
                2. INFORMATION WE COLLECT
              </h2>
              <p className="mb-3" data-id="element-456">
                We collect only the minimum information required to complete
                your transaction:
              </p>
              <ul className="list-none space-y-2 pl-4 mb-3" data-id="element-457">
                <li className="flex gap-2" data-id="element-458">
                  <span className="text-orange-accent" data-id="element-459">—</span>{' '}
                  <strong className="text-white" data-id="element-460">Email address</strong> — to
                  deliver your download link and receipt
                </li>
                <li className="flex gap-2" data-id="element-461">
                  <span className="text-orange-accent" data-id="element-462">—</span>{' '}
                  <strong className="text-white" data-id="element-463">Payment information</strong> —
                  processed securely by our payment processor (we never store
                  card data)
                </li>
                <li className="flex gap-2" data-id="element-464">
                  <span className="text-orange-accent" data-id="element-465">—</span>{' '}
                  <strong className="text-white" data-id="element-466">IP address</strong> — collected
                  automatically by our hosting provider for security purposes
                </li>
              </ul>
              <p data-id="element-467">
                We do <strong className="text-white" data-id="element-468">not</strong> collect your
                name, phone number, physical address, or any identifying
                information beyond what is listed above.
              </p>
            </section>

            <section data-id="element-469">
              <h2 className="font-bebas text-2xl text-white tracking-wide mb-3" data-id="element-470">
                3. WHAT WE DO NOT DO
              </h2>
              <p className="mb-3" data-id="element-471">We will never:</p>
              <ul className="list-none space-y-2 pl-4" data-id="element-472">
                <li className="flex gap-2" data-id="element-473">
                  <span className="text-orange-accent" data-id="element-474">—</span> Sell, rent, or
                  trade your email address to third parties
                </li>
                <li className="flex gap-2" data-id="element-475">
                  <span className="text-orange-accent" data-id="element-476">—</span> Add you to
                  marketing lists without your explicit consent
                </li>
                <li className="flex gap-2" data-id="element-477">
                  <span className="text-orange-accent" data-id="element-478">—</span> Share your
                  purchase history with any government agency absent a valid
                  legal order
                </li>
                <li className="flex gap-2" data-id="element-479">
                  <span className="text-orange-accent" data-id="element-480">—</span> Use tracking
                  pixels or behavioral advertising tools on this page
                </li>
                <li className="flex gap-2" data-id="element-481">
                  <span className="text-orange-accent" data-id="element-482">—</span> Store your
                  payment details on our servers
                </li>
              </ul>
            </section>

            <section data-id="element-483">
              <h2 className="font-bebas text-2xl text-white tracking-wide mb-3" data-id="element-484">
                4. HOW WE USE YOUR INFORMATION
              </h2>
              <p className="mb-3" data-id="element-485">Your email address is used exclusively to:</p>
              <ul className="list-none space-y-2 pl-4" data-id="element-486">
                <li className="flex gap-2" data-id="element-487">
                  <span className="text-orange-accent" data-id="element-488">—</span> Send your
                  purchase confirmation and download link
                </li>
                <li className="flex gap-2" data-id="element-489">
                  <span className="text-orange-accent" data-id="element-490">—</span> Process refund
                  requests if applicable
                </li>
                <li className="flex gap-2" data-id="element-491">
                  <span className="text-orange-accent" data-id="element-492">—</span> Respond to
                  customer support inquiries you initiate
                </li>
              </ul>
            </section>

            <section data-id="element-493">
              <h2 className="font-bebas text-2xl text-white tracking-wide mb-3" data-id="element-494">
                5. PAYMENT PROCESSING
              </h2>
              <p data-id="element-495">
                All payment transactions are processed by a PCI-compliant
                third-party payment processor. We do not have access to your
                full credit card number at any time. Your financial data is
                governed by the payment processor's own privacy policy and
                security standards.
              </p>
            </section>

            <section data-id="element-496">
              <h2 className="font-bebas text-2xl text-white tracking-wide mb-3" data-id="element-497">
                6. COOKIES & TRACKING
              </h2>
              <p data-id="element-498">
                This website uses minimal session cookies required for basic
                functionality. We do not use advertising cookies, cross-site
                tracking, or third-party analytics that identify individual
                users. Any analytics we use are aggregated and anonymized.
              </p>
            </section>

            <section data-id="element-499">
              <h2 className="font-bebas text-2xl text-white tracking-wide mb-3" data-id="element-500">
                7. DATA RETENTION
              </h2>
              <p data-id="element-501">
                Purchase records are retained for the minimum period required by
                applicable tax and financial regulations (typically 7 years).
                After this period, records are permanently deleted. You may
                request deletion of your data at any time by contacting us,
                subject to legal retention requirements.
              </p>
            </section>

            <section data-id="element-502">
              <h2 className="font-bebas text-2xl text-white tracking-wide mb-3" data-id="element-503">
                8. YOUR RIGHTS
              </h2>
              <p className="mb-3" data-id="element-504">You have the right to:</p>
              <ul className="list-none space-y-2 pl-4" data-id="element-505">
                <li className="flex gap-2" data-id="element-506">
                  <span className="text-orange-accent" data-id="element-507">—</span> Request a copy
                  of any personal data we hold about you
                </li>
                <li className="flex gap-2" data-id="element-508">
                  <span className="text-orange-accent" data-id="element-509">—</span> Request
                  correction of inaccurate data
                </li>
                <li className="flex gap-2" data-id="element-510">
                  <span className="text-orange-accent" data-id="element-511">—</span> Request deletion
                  of your data (subject to legal requirements)
                </li>
                <li className="flex gap-2" data-id="element-512">
                  <span className="text-orange-accent" data-id="element-513">—</span> Opt out of any
                  future communications at any time
                </li>
              </ul>
            </section>

            <section data-id="element-514">
              <h2 className="font-bebas text-2xl text-white tracking-wide mb-3" data-id="element-515">
                9. LEGAL DISCLOSURE
              </h2>
              <p data-id="element-516">
                We will only disclose your information to law enforcement or
                government agencies if required by a valid, lawful court order
                or subpoena. We will notify you of any such request to the
                extent permitted by law.
              </p>
            </section>

            <section data-id="element-517">
              <h2 className="font-bebas text-2xl text-white tracking-wide mb-3" data-id="element-518">
                10. CONTACT
              </h2>
              <p data-id="element-519">
                For privacy-related requests or questions, contact us at:{' '}
                <span className="text-orange-accent" data-id="element-520">
                  privacy@concretebunkerblueprint.com
                </span>
              </p>
            </section>
          </div>
        </motion.div>

        {/* Back button bottom */}
        <motion.button onClick={onBack} className="inline-flex items-center gap-2 text-concrete/60 hover:text-orange-accent transition-colors duration-200 mt-16 font-inter text-sm" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} data-id="element-521">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-522" />
          Back to main page
        </motion.button>
      </div>
    </div>;
}